<script setup lang="ts">
import { computed, onErrorCaptured, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import logger from '@/utils/logger'
import SideNav from '@/components/nav/SideNav.vue'
import TopBar from '@/components/nav/TopBar.vue'
import NotificationToast from '@/components/common/NotificationToast.vue'
import KeyboardShortcutsHelp from '@/components/common/KeyboardShortcutsHelp.vue'
import GlobalSearchModal from '@/components/common/GlobalSearchModal.vue'
import NotificationCenter from '@/components/common/NotificationCenter.vue'
import ChatBubble from '@/components/chat/ChatBubble.vue'
import ChatPanel from '@/components/chat/ChatPanel.vue'
import AiBubble from '@/components/ai/AiBubble.vue'
import AiChatPanel from '@/components/ai/AiChatPanel.vue'
import { useAuthStore } from '@/stores/auth'
import TabBar from '@/components/tabs/TabBar.vue'
import { useTabStore } from '@/stores/tabs'
import { useNavigation } from '@/composables/useNavigation'
import { useMobileMenu } from '@/composables/useMobileMenu'
import { useRealtimeConnection } from '@/composables/useRealtimeConnection'
import { useNotificationSounds } from '@/composables/useNotificationSounds'
import { useTabNotifications } from '@/composables/useTabNotifications'
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts'
import { Loader2, RefreshCw } from 'lucide-vue-next'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
useNavigation() // Initialize nav state
const { isMobile, mobileMenuOpen, closeMenu } = useMobileMenu()
const tabStore = useTabStore()
useRealtimeConnection()
useNotificationSounds()
useTabNotifications()

// Auto-create a default tab for the current page so the tab bar is always visible
function ensureDefaultTab() {
  if (tabStore.tabCount === 0 && authStore.isAuthenticated) {
    tabStore.addTab(route.path, buildRouteTitle())
  }
}
onMounted(ensureDefaultTab)
watch(() => authStore.isAuthenticated, (authed) => { if (authed) ensureDefaultTab() })

// Strip _t= query param to get the base path for comparison
function basePath(p: string): string {
  return p.replace(/[?&]_t=tab-\d+/, '').replace(/\?$/, '')
}

// Build a meaningful tab title from the current route
function buildRouteTitle(): string {
  const name = route.name as string | undefined
  const id = route.params?.id as string | undefined

  // Detail views — include the ID for context
  if (id && name) {
    const titleMap: Record<string, string> = {
      'TicketDetail': `Ticket #${id}`,
      'ProjectDetail': `Project #${id}`,
      'ClientProjectDetail': `Project #${id}`,
      'OrderDetail': `Order #${id}`,
      'ReportBuilderEdit': `Report #${id}`,
    }
    if (titleMap[name]) return titleMap[name]
  }

  // Use meta title if set, otherwise humanize the route name
  return (route.meta?.title as string)
    || (typeof name === 'string' ? name.replace(/([a-z])([A-Z])/g, '$1 $2') : '')
    || 'Page'
}

// Keep tabs in sync with navigation — when navigating via sidebar/links,
// update the active tab's path or create a new tab if needed
watch(() => route.fullPath, (newFullPath) => {
  if (!authStore.isAuthenticated || tabStore.tabCount === 0) return
  const newBase = basePath(newFullPath)
  // If the active tab already matches this path, nothing to do
  if (tabStore.activeTab && basePath(tabStore.activeTab.path) === newBase) return
  // Check if any existing non-duplicate tab matches the new path
  const existing = tabStore.tabs.find(t => basePath(t.path) === newBase)
  if (existing) {
    tabStore.setActiveTab(existing.id)
  } else {
    const title = buildRouteTitle()
    if (tabStore.activeTab && tabStore.activeTab.closable && !tabStore.activeTab.pinned) {
      tabStore.updateTabPath(tabStore.activeTab.id, newFullPath, title)
    } else {
      // Active tab is pinned — create a new tab for this navigation
      tabStore.addTab(newFullPath, title)
    }
  }
})

// -- Keyboard shortcuts --
const showShortcutsHelp = ref(false)
const showGlobalSearch = ref(false)
const searchTabMode = ref(false)
const { register } = useKeyboardShortcuts()

register([
  {
    key: '?',
    shift: true,
    handler: () => { showShortcutsHelp.value = !showShortcutsHelp.value },
    description: 'Open keyboard shortcuts help',
    category: 'General',
  },
  {
    key: 'g t',
    handler: () => { router.push('/tickets') },
    description: 'Go to Tickets',
    category: 'Navigation',
  },
  {
    key: 'g p',
    handler: () => { router.push('/projects') },
    description: 'Go to Projects',
    category: 'Navigation',
  },
  {
    key: 'g d',
    handler: () => { router.push('/') },
    description: 'Go to Dashboard',
    category: 'Navigation',
  },
  {
    key: 'g h',
    handler: () => { router.push('/help') },
    description: 'Go to Help',
    category: 'Navigation',
  },
  {
    key: '/',
    handler: () => { showGlobalSearch.value = true },
    description: 'Open global search',
    category: 'General',
  },
  {
    key: 'k',
    ctrl: true,
    global: true,
    handler: () => { showGlobalSearch.value = true },
    description: 'Open command palette',
    category: 'General',
  },
])
const showLayout = computed(() => route.meta.layout !== 'blank' && authStore.isAuthenticated)
const canViewChat = computed(() => authStore.hasPermission('chat.view'))
const canViewAiChat = computed(() => authStore.hasPermission('ai.chat'))

// Error handling for component load failures
const componentError = ref<Error | null>(null)

onErrorCaptured((err) => {
  logger.error('[MainLayout] Component error captured:', err)

  // Check for chunk load failures
  if (err.message?.includes('Failed to fetch dynamically imported module') ||
      err.message?.includes('Loading chunk') ||
      err.message?.includes('Loading CSS chunk')) {
    logger.warn('[MainLayout] Chunk load failed, reloading...')
    window.location.reload()
    return false
  }
  
  componentError.value = err as Error
  return false
})

const handleRetry = () => {
  componentError.value = null
  router.go(0)
}
</script>

<template>
  <div class="min-h-screen bg-surface-950">
    <NotificationToast />
    <KeyboardShortcutsHelp :show="showShortcutsHelp" @close="showShortcutsHelp = false" />
    <GlobalSearchModal :show="showGlobalSearch" :tab-mode="searchTabMode" @close="showGlobalSearch = false; searchTabMode = false" />
    <NotificationCenter />
    <template v-if="showLayout">
      <SideNav />
      <!-- Mobile backdrop overlay -->
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="isMobile && mobileMenuOpen" class="mobile-backdrop" @click="closeMenu" />
      </Transition>
      <ChatBubble v-if="canViewChat" />
      <ChatPanel v-if="canViewChat" />
      <AiBubble v-if="canViewAiChat" />
      <AiChatPanel v-if="canViewAiChat" />
      <div class="min-h-screen flex flex-col transition-all duration-200" :class="isMobile ? 'ml-0' : 'ml-[60px]'">
        <TopBar />
        <TabBar @open-search="searchTabMode = true; showGlobalSearch = true" />
        <main class="flex-1 p-4 md:p-6">
          <!-- Error State -->
          <div v-if="componentError" class="flex flex-col items-center justify-center h-64 text-center">
            <p class="text-surface-400 mb-4">Something went wrong loading this page.</p>
            <button @click="handleRetry" class="btn-primary">
              <RefreshCw class="w-4 h-4 mr-2" />
              Retry
            </button>
          </div>

          <!-- Router View with KeepAlive -->
          <router-view v-else v-slot="{ Component, route: viewRoute }">
            <keep-alive :max="20">
              <component :is="Component" :key="viewRoute.fullPath" />
            </keep-alive>
          </router-view>
        </main>
      </div>
    </template>
    <template v-else>
      <router-view />
    </template>
  </div>
</template>
